import {useEffect} from 'react';
import Router, {NextRouter} from 'next/router';

const restorationBlacklist = [/\/touchpoints\/.*\/feedback$/];

const shouldRestore = (url: string) => {
  return !restorationBlacklist.some((regex) => url.match(regex));
};

let globalScrollPositions = {};

const hasSessionStorage = () => {
  try {
    return Boolean(window.sessionStorage);
  } catch (error) {
    return false;
  }
};

const saveScrollPos = (url: string) => {
  const scrollPos = {x: window.scrollX, y: window.scrollY};

  if (hasSessionStorage()) {
    sessionStorage.setItem(url, JSON.stringify(scrollPos));
  } else {
    globalScrollPositions[url] = scrollPos;
  }
};

const restoreScrollPos = (url: string) => {
  const scrollPos = hasSessionStorage() ? JSON.parse(sessionStorage.getItem(url)) : globalScrollPositions[url];

  if (scrollPos) {
    window.scrollTo(scrollPos.x, scrollPos.y);
  }
};

const useScrollRestoration = (router: NextRouter) => {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      let shouldScrollRestore = false;
      window.history.scrollRestoration = 'manual';

      if (shouldRestore(router.asPath)) {
        restoreScrollPos(router.asPath);
      }

      const onBeforeUnload = (event) => {
        saveScrollPos(router.asPath);
        delete event['returnValue'];
      };

      const onRouteChangeStart = () => {
        saveScrollPos(router.asPath);
      };

      const onRouteChangeComplete = (url) => {
        if (shouldScrollRestore) {
          shouldScrollRestore = false;
          restoreScrollPos(url);
        }
      };

      window.addEventListener('beforeunload', onBeforeUnload);
      Router.events.on('routeChangeStart', onRouteChangeStart);
      Router.events.on('routeChangeComplete', onRouteChangeComplete);

      Router.beforePopState(() => {
        shouldScrollRestore = true;
        return true;
      });

      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        Router.events.off('routeChangeStart', onRouteChangeStart);
        Router.events.off('routeChangeComplete', onRouteChangeComplete);

        Router.beforePopState(() => true);
      };
    }
  }, [router]);
};

export default useScrollRestoration;
